import type { PropsWithChildren, ReactNode } from "react";
import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardFooter } from "@design-lab/shared/ui/card";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { Button, SpaceHolder } from "@design-lab/shared/ui";
import { ChevronRight, PlusCircle, Copy, CheckCircle } from "lucide-react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { ProductBrowser } from "@design-lab/widgets/product-browser";
import { useDesignLabConfig, useIsSample } from "@design-lab/shared/lib/config";
import {
  useCompositionsList,
  useCurrentComposition,
  useCompositionForm
} from "@design-lab/entities/composition";
import { useImagesFastCache, useArtworkCopy } from "@design-lab/entities/artwork";
import type { Side } from "@design-lab/shared";
import { CompositionInfo } from "./CompositionInfo";
import { CompositionsCarousel } from "./CompositionsCarousel";

type TabCardProps = PropsWithChildren<{
  className?: string;
  contentClassName?: string;
  showAddProductButton?: boolean;
  hideFooter?: boolean;
  title?: string;
  showNextButton?: boolean;
  footer?: ReactNode;
}>;

export function TabCard(props: TabCardProps) {
  const {
    children,
    className,
    contentClassName,
    showAddProductButton,
    hideFooter = false,
    title,
    showNextButton = false,
    footer
  } = props;
  const { isDesktop } = useResponsive();
  const { isValid, canGoNext, canBeSkipped, next } = useTab();
  const { tab, isDesignTab } = useTabNavigation();
  const [isProductBrowserOpen, setIsProductBrowserOpen] = useState(false);
  const isSample = useIsSample();
  const config = useDesignLabConfig();
  const madeSamplesV1 = isSample && config.flip?.madeSamplesV1;

  const { compositions } = useCompositionsList();
  const { form } = useCompositionForm();
  const { currentComposition } = useCurrentComposition();

  const currentSide: Side | undefined =
    isDesignTab && (tab === "front" || tab === "back") ? tab : undefined;
  const { copyArtworkToOtherSide, shouldShowCopyButton, isShowingCopySuccess } =
    useArtworkCopy(currentSide);

  const onCopyToOtherSide = useCallback(() => {
    if (isShowingCopySuccess) return;
    try {
      copyArtworkToOtherSide();
    } catch (error) {
      window.Honeybadger?.notify(`[Design Lab] Failed to copy artwork`, {
        context: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          component: "TabCard",
          action: "copyArtworkToOtherSide",
          currentSide,
          tab
        }
      });
    }
  }, [copyArtworkToOtherSide, currentSide, tab, isShowingCopySuccess]);

  const imagesToCache = useMemo(
    () =>
      compositions.flatMap(c => [
        c.surfaces.back?.picture,
        c.surfaces.front?.picture,
        c.surfaces.frontLeftChest?.picture
      ]),
    [compositions]
  );

  useImagesFastCache(imagesToCache);

  const handleCompositionSelect = (id: string) => {
    form.setValue("currentCompositionId", id);
  };
  const showDesktopCardFooter =
    isDesktop &&
    !hideFooter &&
    (canGoNext || canBeSkipped || (showAddProductButton && madeSamplesV1));
  const showMobileCardFooter = !isDesktop && showNextButton;

  const openProductBrowser = useCallback(
    () => setIsProductBrowserOpen(true),
    [setIsProductBrowserOpen]
  );
  const closeProductBrowser = useCallback(
    () => setIsProductBrowserOpen(false),
    [setIsProductBrowserOpen]
  );

  return (
    <Card
      className={cn(
        "flex size-full flex-col border-none shadow-none lg:border lg:shadow",
        className
      )}
    >
      <CardContent
        className={cn("flex flex-1 flex-col overflow-hidden p-0 lg:p-4", contentClassName)}
      >
        {isDesktop && (title || showAddProductButton) && (
          <div className="mb-2 flex items-center justify-between gap-2">
            {title && <h2 className="hidden text-xl font-semibold lg:block">{title}</h2>}

            {showAddProductButton && !isSample && (
              <Button
                data-testid="add-products-button"
                onPress={openProductBrowser}
                className="bg-blue-700 hover:bg-blue-800"
              >
                <PlusCircle className="size-6" /> Add More Products
              </Button>
            )}
          </div>
        )}

        {isDesktop && (
          <CompositionsCarousel
            compositions={compositions}
            selectedId={currentComposition?.id}
            onSelect={handleCompositionSelect}
            onAddMoreProduct={
              showAddProductButton && !isDesktop && !isSample ? openProductBrowser : undefined
            }
          />
        )}

        <div className="border-primary flex flex-1 flex-col gap-2 overflow-y-auto border-2 p-2.5">
          <CompositionInfo />
          {shouldShowCopyButton && (
            <Button
              data-testid="copy-to-other-side-button-tabcard"
              size="lg"
              variant="outline"
              onPress={onCopyToOtherSide}
              isDisabled={isShowingCopySuccess}
              className="w-full border-black/20 py-2 text-sm"
            >
              <SpaceHolder>
                <SpaceHolder.Item
                  visible={isShowingCopySuccess}
                  className="flex items-center justify-center gap-2"
                >
                  <CheckCircle className="size-4 text-blue-600" />
                  Copied
                </SpaceHolder.Item>
                <SpaceHolder.Item
                  visible={!isShowingCopySuccess}
                  className="flex items-center justify-center gap-2"
                >
                  <Copy className="size-4 text-blue-600" />
                  Copy to Other Side
                </SpaceHolder.Item>
              </SpaceHolder>
            </Button>
          )}
          {children}
        </div>
      </CardContent>
      {(showDesktopCardFooter || showMobileCardFooter || footer) && (
        <CardFooter
          className={cn("flex items-center justify-between gap-2 p-3", {
            "p-0 pt-2": showMobileCardFooter
          })}
        >
          {showDesktopCardFooter && (
            <>
              <div className="flex items-center gap-2">
                {canGoNext && (
                  <Button
                    data-testid="skip-button"
                    variant="destructive"
                    size="xl"
                    isDisabled={!isValid}
                    onPress={next}
                    className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
                  >
                    Next <ChevronRight />
                  </Button>
                )}

                {canBeSkipped && (
                  <Button data-testid="next-button" size="xl" variant="outline" onPress={next}>
                    Skip
                  </Button>
                )}
              </div>
            </>
          )}

          {footer}
        </CardFooter>
      )}
      <ProductBrowser isOpen={isProductBrowserOpen} onClose={closeProductBrowser} />
    </Card>
  );
}
